import React, { forwardRef } from 'react';
import { FormControl, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import CustomerCancellationRuleSet from '../../../interfaces/output/customerCancellationRuleSet';
import LanguageUtils from '../../../utils/LanguageUtils';
import { isCreatingCustomerCancellationRuleSet, isLoadingCustomerCancellationRuleSetById, isUpdatingCustomerCancellationRuleSet } from '../../../reducers/cancellationRuleSetReducer';
import WorkflowUtils from '../../../utils/workflowUtils';
import Loader from '../../common/widgets/loader';
import GenericTextField from '../../common/widgets/genericTextField';
import GenericDatePicker from '../../common/widgets/genericDatePicker';
import CustomerCancellationRuleSetPriorityAndValidityValidation from './customerCancellationRuleSetPriorityAndValidityValidation';

interface AddCustomerCustomerCancellationRuleSetStep3Props {
    customerCancellationRuleSet: CustomerCancellationRuleSet;
    onChange: any;
    showValidationError?: boolean;
}

const ATTRIBUTES = {
    PRIORITY_RANK: 'priorityRank',
    VALID_FROM: 'validFromDate',
    VALID_TO: 'validToDate'
};

const messages = {
    validFrom: LanguageUtils.createMessage('Valid from'),
    validTo: LanguageUtils.createMessage('Valid to'),
    priorityRank: LanguageUtils.createMessage('Priority rank')
};

const AddCustomerCustomerCancellationRuleSetStep3 = forwardRef((props: AddCustomerCustomerCancellationRuleSetStep3Props, ref: any): JSX.Element => {
    const { onChange, customerCancellationRuleSet, showValidationError } = props;
    const isLoadingObjects = [useSelector(isLoadingCustomerCancellationRuleSetById), useSelector(isCreatingCustomerCancellationRuleSet), useSelector(isUpdatingCustomerCancellationRuleSet)];
    const isLoading = isLoadingObjects.find((obj: boolean) => obj === true);
    WorkflowUtils.setHandle(ref, null);

    const setValidationState = (attribute: string) => {
        let isValid = true;

        if(!showValidationError || !attribute) {
            return undefined;
        }

        switch (attribute) {
            case ATTRIBUTES.PRIORITY_RANK: {
                isValid = CustomerCancellationRuleSetPriorityAndValidityValidation.validateInputString(customerCancellationRuleSet?.priorityRank?.toString());
                break;
            }
            case ATTRIBUTES.VALID_FROM: {
                isValid = CustomerCancellationRuleSetPriorityAndValidityValidation.validateRequiredDate(customerCancellationRuleSet.validFromDate) &&
                    CustomerCancellationRuleSetPriorityAndValidityValidation.validateDateFormat(customerCancellationRuleSet.validFromDate) &&
                    CustomerCancellationRuleSetPriorityAndValidityValidation.validateInputDateStart(customerCancellationRuleSet);
                break;
            }
            case ATTRIBUTES.VALID_TO: {
                isValid = CustomerCancellationRuleSetPriorityAndValidityValidation.validateInputDateEnd(customerCancellationRuleSet) &&
                    CustomerCancellationRuleSetPriorityAndValidityValidation.validateDateFormat(customerCancellationRuleSet.validToDate);
                break;
            }
        }

        return !isValid;
    };

    return (
        <Loader isLoading={isLoading}>
            <Grid container spacing={2} mt={2}>
                <Grid item sm={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericTextField<number>
                            name="priorityRank"
                            required
                            error={setValidationState(ATTRIBUTES.PRIORITY_RANK)}
                            label={messages.priorityRank}
                            type="number"
                            value={customerCancellationRuleSet.priorityRank}
                            onChange={onChange}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericDatePicker
                            name="validFromDate"
                            label={messages.validFrom}
                            required
                            error={setValidationState(ATTRIBUTES.VALID_FROM)}
                            value={customerCancellationRuleSet?.validFromDate}
                            onChange={onChange}
                            type="start"
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={3} />
                <Grid item xs={3} />
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <GenericDatePicker
                            name="validToDate"
                            label={messages.validTo}
                            value={customerCancellationRuleSet?.validToDate}
                            error={setValidationState(ATTRIBUTES.VALID_TO)}
                            onChange={onChange}
                            type="now"
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={3} />
            </Grid>
        </Loader>
    );
});

export default AddCustomerCustomerCancellationRuleSetStep3;